import React, { useContext, useRef, useState } from 'react';
import {
  Banner,
  Button,
  Flex,
  Input,
  Modal,
  ModalManagerContext,
  ModalMethods,
  Table,
  Text,
} from '@pega/cosmos-react-core';
import useLocalTranslation from '../../hooks/useLocalTranslation';
import { useDispatch, useSelector } from 'react-redux';
import { appState, confirmationAlertProps } from '../../interfaces/common';
import { updateErrorWebhook } from '../../redux/actions';
import styled, { css } from 'styled-components';
import { ConfirmationAlert } from '../Utilities/CustomModal';
import DisplayWebhooks from '../Utilities/DisplayWebhooks';

const columns: any = [
  {
    renderer: 'field',
    label: 'Field',
  },
  {
    renderer: 'type',
    label: 'Type',
  },
  {
    renderer: 'description',
    label: 'Description',
  },
];

const data = [
  { id: 1, field: 'ErrorCode', type: 'Number', description: 'The error code associated with the error (e.g: "500")' },
  { id: 2, field: 'ErrorMessage', type: 'String', description: 'The error message detailing the nature of error' },
  { id: 3, field: 'Timestamp', type: 'String', description: 'The timestamp when the error occured in ISO 8601 format' },
  { id: 4, field: 'ConnectionType', type: 'String', description: 'The type of connecion where the error occured' },
  { id: 5, field: 'ConnectionName', type: 'String', description: 'The Name of Specific Connection' },
  { id: 6, field: 'AdditionalInfo', type: 'Object', description: 'Any additional info related to error and payload' },
];

export const StyledLinkHover = styled.h5(
  () =>
    css`
      :hover {
        text-decoration: underline;
      }
    `,
);

export const linkTextStyle = {
  width: 'fit-content',
  cursor: 'pointer',
  color: '#0076D1',
  fontSize: '0.8125rem',
  paddingRight: '5px',
};

interface editWehbookProps {
  selectedWebhook: string;
  updateWebhook: Function;
  index: number;
}

const Alerts = () => {
  const { errorWebhooks = [] } = useSelector((state: appState) => state);
  const translate = useLocalTranslation();
  const dispatch = useDispatch();
  const [webhook, setWebhook] = useState('');
  const [webhookError, setWebhookError] = useState('');
  const { create } = useContext(ModalManagerContext);
  const deleteModalMethods = useRef<ModalMethods<Partial<confirmationAlertProps>>>();
  const editModalMethods = useRef<ModalMethods<Partial<editWehbookProps>>>();
  const updatedWebhookList = JSON.parse(JSON.stringify(errorWebhooks));
  const onWebhookChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWebhookError('');
    setWebhook(event.target.value);
  };
  const updateWebhook = async () => {
    if (isValidUrl(webhook) && !errorWebhooks.includes(webhook)) {
      updatedWebhookList.push(webhook.trim());
      setWebhook('');
      dispatch(updateErrorWebhook(updatedWebhookList));
    } else {
      setWebhookError(errorWebhooks.includes(webhook) ? translate('existing_error') : translate('invalid_webhook'));
    }
  };

  const deleteConfirmation = (index: number) => {
    deleteModalMethods.current = create(
      ConfirmationAlert,
      { isErroWebhook: true, webhookToBeDeleted: updatedWebhookList[index], deleteChannel: deleteWebhook },
      { alert: true },
    );
  };

  const deleteWebhook = async (webhookToBeDeleted?: string) => {
    dispatch(updateErrorWebhook(updatedWebhookList.filter((val: string) => val !== webhookToBeDeleted)));
  };

  const editWebhook = (index: number) => {
    editModalMethods.current = create(
      EditWebhookModal,
      { index, selectedWebhook: updatedWebhookList[index], updateWebhook: updateExistingWebhook },
      { alert: true },
    );
  };

  const updateExistingWebhook = async (updatedWebhook: string, index: number) => {
    updatedWebhookList[index] = updatedWebhook;
    dispatch(updateErrorWebhook(updatedWebhookList));
  };
  return (
    <>
      <Flex
        container={{
          direction: 'column',
          itemGap: 1,
          pad: 3,
        }}
      >
        {errorWebhooks.length === 10 && (
          <Banner id="warning" variant="warning" messages={[translate('maximum_limit_warning')]} />
        )}
        <Text variant="h1">{translate('alerts_webhook')}</Text>
        <Text>{translate('alert_desc')}</Text>
      </Flex>
      <Flex
        container={{
          direction: 'column',
          itemGap: 2,
          pad: 3,
        }}
      >
        <Input
          label={translate('webhook_url')}
          type="text"
          autoComplete="off"
          value={webhook}
          onChange={onWebhookChange}
          info={webhookError}
          required={true}
          status={webhookError ? 'error' : undefined}
          disabled={errorWebhooks.length === 10}
        />
        <Flex
          container={{
            alignItems: 'start',
          }}
        >
          <Button
            name="Save"
            type="submit"
            variant="primary"
            onClick={updateWebhook}
            disabled={Boolean(webhookError || !webhook)}
          >
            {translate('save')}
          </Button>
        </Flex>
      </Flex>
      <DisplayWebhooks webhooks={errorWebhooks} editWebhook={editWebhook} deleteConfirmation={deleteConfirmation} />
      <Flex
        container={{
          direction: 'column',
          itemGap: 2,
          pad: 3,
        }}
      >
        <Table title={translate('error_types')} columns={columns} data={data} />
      </Flex>
    </>
  );
};
const isValidUrl = (url: string): boolean => {
  const urlRegex = /^(?:(?:https?|ftp):\/\/)?(?:www\.)?[a-z0-9-]+(?:\.[a-z0-9-]+)+[^\s]*$/i;
  return urlRegex.test(url.trim());
};
const EditWebhookModal: React.FC<editWehbookProps> = ({ selectedWebhook, updateWebhook, index }) => {
  const { errorWebhooks = [] } = useSelector((state: appState) => state);
  const translate = useLocalTranslation();
  const { ModalContext } = useContext(ModalManagerContext);
  const { dismiss } = useContext(ModalContext);
  const [webhook, setWebhook] = useState(selectedWebhook);
  const [webhookError, setWebhookError] = useState('');
  const onWebhookChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWebhook(event.target.value);
    setWebhookError('');
  };
  const save = () => {
    if (isValidUrl(webhook) && !errorWebhooks.includes(webhook)) {
      updateWebhook(webhook, index);
      dismiss();
    } else {
      setWebhookError(errorWebhooks.includes(webhook) ? translate('existing_error') : translate('invalid_webhook'));
    }
  };

  return (
    <Modal
      center={true}
      heading=""
      autoWidth={true}
      actions={
        <>
          <Button onClick={dismiss}>{translate('cancel')}</Button>
          <Button variant="primary" onClick={save} disabled={Boolean(webhookError || !webhook)}>
            {translate('ok')}
          </Button>
        </>
      }
    >
      <Input
        label={translate('webhook_url')}
        type="text"
        autoComplete="off"
        value={webhook}
        onChange={onWebhookChange}
        info={webhookError}
        required={true}
        status={webhookError ? 'error' : undefined}
      />
    </Modal>
  );
};

export default Alerts;
